import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Cpu, Keyboard, LogOut, Monitor, Moon, Palette, Shield, Sun, User } from "lucide-react";
import { Button } from "@/client/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/client/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/client/components/ui/alert-dialog";
import { APP_STYLES, useAppStyle, type AppStyle } from "@/client/hooks/use-app-style";
import { useSession } from "@/client/hooks/use-session";
import { useTheme, type Theme } from "@/client/hooks/use-theme";
import { downloadDrafts, type StoredDraft } from "@/client/db/drafts";
import { GpuWorkerDialog } from "@/client/components/art/gpu-worker-dialog";
import { openShortcuts } from "./shortcuts-dialog";

/** Light, dark, or the device's; and the app's look, kept open to compare */
function AppearanceItems() {
  const { theme, setTheme } = useTheme();
  const { style, setStyle } = useAppStyle();
  return (
    <>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          {theme === "dark" ? <Moon className="mr-2 h-4 w-4" /> : theme === "light" ? <Sun className="mr-2 h-4 w-4" /> : <Monitor className="mr-2 h-4 w-4" />}
          Light or dark
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          <DropdownMenuRadioGroup value={theme} onValueChange={(value) => setTheme(value as Theme)}>
            <DropdownMenuRadioItem value="light">
              <Sun />
              Light
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="dark">
              <Moon />
              Dark
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="system">
              <Monitor />
              Same as this device
            </DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuSubContent>
      </DropdownMenuSub>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <Palette className="mr-2 h-4 w-4" />
          App style
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="w-72">
          <DropdownMenuRadioGroup value={style} onValueChange={(value) => setStyle(value as AppStyle)}>
            {APP_STYLES.map((option) => (
              <DropdownMenuRadioItem
                key={option.id}
                value={option.id}
                // Keep the menu open so styles can be compared one after another
                onSelect={(e) => e.preventDefault()}
                className="gap-3 py-2"
              >
                <span className="flex shrink-0 -space-x-1.5" aria-hidden>
                  {option.swatches.map((color) => (
                    <span
                      key={color}
                      className="h-4 w-4 rounded-full border border-foreground/15"
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </span>
                <span className="flex min-w-0 flex-col">
                  <span className="font-medium">{option.name}</span>
                  <span className="truncate text-xs text-muted-foreground">{option.tagline}</span>
                </span>
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    </>
  );
}

export function UserMenu() {
  const { user, isAdmin, signOut, unsavedDrafts } = useSession();
  const navigate = useNavigate();
  const [pendingDrafts, setPendingDrafts] = useState<StoredDraft[] | null>(null);
  const [gpuOpen, setGpuOpen] = useState(false);

  const finishSignOut = async () => {
    setPendingDrafts(null);
    await signOut();
    await navigate({ to: "/signin" });
  };

  // Signing out clears this user's drafts from the browser, so offer them first
  const handleSignOut = () => {
    const drafts = unsavedDrafts();
    if (drafts.length > 0) setPendingDrafts(drafts);
    else void finishSignOut();
  };

  if (!user) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Account">
            <User className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-52">
          <DropdownMenuLabel className="font-normal">
            <div className="text-sm font-medium">{user.name}</div>
            <div className="text-xs text-muted-foreground">{user.email}</div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <AppearanceItems />
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild><Link to="/agents">Connected agents</Link></DropdownMenuItem>
          {isAdmin && (
            <DropdownMenuItem asChild>
              <Link to="/admin">
                <Shield className="mr-2 h-4 w-4" />
                Administration
              </Link>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={openShortcuts}>
            <Keyboard className="mr-2 h-4 w-4" />
            Keyboard shortcuts
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setGpuOpen(true)}>
            <Cpu className="mr-2 h-4 w-4" />
            GPU worker
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleSignOut}>
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {gpuOpen && <GpuWorkerDialog open onOpenChange={setGpuOpen} />}

      <AlertDialog open={pendingDrafts !== null} onOpenChange={(open) => !open && setPendingDrafts(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved writing on this device</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDrafts?.length === 1
                ? "One scene has writing that hasn't reached the server."
                : `${pendingDrafts?.length} scenes have writing that hasn't reached the server.`}{" "}
              Signing out removes it from this browser. Download it first to keep a copy.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button variant="outline" onClick={() => void finishSignOut()}>
              Sign out without it
            </Button>
            <AlertDialogAction
              onClick={() => {
                if (pendingDrafts) downloadDrafts(pendingDrafts);
                void finishSignOut();
              }}
            >
              Download and sign out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
